import { Alert, Button, Card, CardContent, Grid, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { useEffect, useState } from "react";

import { api } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { ErrorState, LoadingState, PageHeader } from "../components";
import { applyServerErrors, FormSection, FormSelect, FormShell, FormText, useAppForm } from "../components/Form";
import { z } from "zod";
import { NotificationPreferences } from "../workflow_notifications/NotificationCenter";
import help from "./HELP.md?raw";
import { useProfile, useUpdateProfile } from "./useData";

const schema = z.object({
  first_name: z.string().min(1, "Required").max(80),
  last_name: z.string().max(80),
  timezone: z.string().min(1, "Required"),
  locale: z.string(),
  phone: z.string().max(40),
});
type Values = z.infer<typeof schema>;

const LOCALES = [["en-US", "English (US)"], ["en-GB", "English (UK)"], ["de-DE", "Deutsch"], ["es-MX", "Español (MX)"], ["fr-FR", "Français"]];
const TIMEZONES = Intl.supportedValuesOf ? Intl.supportedValuesOf("timeZone") : ["UTC"];

export function Profile() {
  const { me, refresh } = useAuth();
  const profile = useProfile();
  const update = useUpdateProfile();
  const [error, setError] = useState<string | null>(null);
  const [verificationSent, setVerificationSent] = useState(false);
  const form = useAppForm(schema, { defaultValues: { first_name: "", last_name: "", timezone: "UTC", locale: "en-US", phone: "" } });

  useEffect(() => {
    if (profile.data) form.reset({ first_name: profile.data.first_name, last_name: profile.data.last_name, timezone: profile.data.timezone || "UTC", locale: profile.data.locale || "en-US", phone: profile.data.phone ?? "" });
  }, [profile.data, form]);

  async function onSubmit(values: Values) {
    setError(null);
    try {
      await update.mutateAsync(values);
      form.reset(values);
      await refresh();
    } catch (err) {
      setError(applyServerErrors(form, err));
    }
  }

  if (profile.isLoading) return <LoadingState />;
  if (profile.isError) return <ErrorState message={profile.error.message} onRetry={() => void profile.refetch()} />;
  return (
    <>
      <PageHeader help={help} title="My profile" subtitle={profile.data?.email} crumbs={[{ label: "Settings" }, { label: "Profile" }]} />
      {me && !me.email_verified && (
        <Alert severity="warning" sx={{ mb: 2 }} action={
          <Button color="inherit" size="small" disabled={verificationSent} onClick={async () => { await api.POST("/api/v1/accounts/auth/verify/request/"); setVerificationSent(true); }}>
            {verificationSent ? "Email sent" : "Resend verification"}
          </Button>}>
          Your email address is not verified yet.
        </Alert>
      )}
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 8 }}>
          <FormShell form={form} onSubmit={onSubmit} error={error} busy={update.isPending}>
            <FormSection title="Personal details">
              <FormText name="first_name" label="First name" />
              <FormText name="last_name" label="Last name" />
              <FormText name="phone" label="Phone" />
              <TextField label="Email" value={profile.data?.email ?? ""} disabled helperText="Contact support to change your sign-in email." />
            </FormSection>
            <FormSection title="Locale">
              <FormSelect name="timezone" label="Timezone" options={TIMEZONES.map((t) => ({ value: t, label: t }))} />
              <FormSelect name="locale" label="Language & number format" options={LOCALES.map(([v, l]) => ({ value: v, label: l }))} />
            </FormSection>
          </FormShell>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card><CardContent>
            <Typography variant="subtitle2" gutterBottom>Preferences</Typography>
            <Stack spacing={2}>
              <TextField select label="Table density" value={String(profile.data?.ui_preferences?.density ?? "compact")}
                onChange={(e) => update.mutate({ ui_preferences: { density: e.target.value } })}>
                <MenuItem value="compact">Compact</MenuItem><MenuItem value="standard">Standard</MenuItem>
              </TextField>
              <TextField select label="Email digest" value={String(profile.data?.notification_preferences?.email_digest ?? "daily")}
                onChange={(e) => update.mutate({ notification_preferences: { email_digest: e.target.value } })}>
                <MenuItem value="daily">Daily</MenuItem><MenuItem value="weekly">Weekly</MenuItem><MenuItem value="off">Off</MenuItem>
              </TextField>
            </Stack>
          </CardContent></Card>
        </Grid>
        <Grid size={12}>
          <Card><CardContent>
            <Typography variant="subtitle2" gutterBottom>Notifications</Typography>
            <NotificationPreferences />
          </CardContent></Card>
        </Grid>
      </Grid>
    </>
  );
}
